import { useMemo } from 'react';
import type {
  AllAssortmentExclusionsQuery,
  AllVesselAttributeAssortmentExclusionsQuery,
  Assortment,
  AssortmentGroup,
} from '../../graphql';
import {
  useAllAssortmentExclusionsQuery,
  useAllVesselAttributeAssortmentExclusionsQuery,
} from '../../graphql';
import { NONE, filteredMap, isDefined } from '../../lib/util';
import { compact } from 'lodash';
import { mapGroupBy } from '@seaber/common/lib/collections';

export type ExclusionAssortment = Required<
  Pick<Assortment | AssortmentGroup, '__typename' | 'id' | 'name'>
>;

export type VoyageExclusionItem = {
  previous: ExclusionAssortment;
  next: ExclusionAssortment;
  voyages?: number | null;
};

export type AssortmentExclusionKey = `${'A' | 'G'}${string}-${
  | 'A'
  | 'G'}${string}`;

export type AssortmentExclusionsMap = Map<
  AssortmentExclusionKey,
  VoyageExclusionItem
>;

export type VesselExclusionItem = {
  vesselAttribute: { name: string; id: number };
  exclusion: {
    __typename: 'Assortment' | 'AssortmentGroup';
    name: string;
    id: number;
  };
};

export type AssortmentExclusionItem = VoyageExclusionItem | VesselExclusionItem;

export function useAssortmentExclusions(skip = false) {
  const { data: assortmentExclusions } = useAllAssortmentExclusionsQuery({
    skip,
  });
  const { data: vesselAttributeAssortmentExclusions } =
    useAllVesselAttributeAssortmentExclusionsQuery({
      skip,
    });

  return useMemo(
    () =>
      parseAssortmentExclusions(
        assortmentExclusions,
        vesselAttributeAssortmentExclusions,
      ),
    [assortmentExclusions, vesselAttributeAssortmentExclusions],
  );
}

export type UseAssortmentExclusionsValue = ReturnType<
  typeof useAssortmentExclusions
>;

export function parseAssortmentExclusions(
  assortmentExclusions: AllAssortmentExclusionsQuery | undefined,
  vesselAttributeAssortmentExclusions:
    | AllVesselAttributeAssortmentExclusionsQuery
    | undefined,
) {
  const voyageExclusionsMap: AssortmentExclusionsMap = new Map();

  const allVoyageExclusions =
    assortmentExclusions?.allAssortmentExclusions?.nodes.filter(isDefined) ??
    [];

  allVoyageExclusions.forEach((ae) => {
    const {
      assortmentByPreviousAssortment: pa,
      assortmentGroupByPreviousAssortmentGroup: pag,
      assortmentByNextAssortment: na,
      assortmentGroupByNextAssortmentGroup: nag,
      voyages,
    } = ae;

    const previous: ExclusionAssortment | null = pa
      ? { __typename: 'Assortment', ...pa }
      : pag
        ? { __typename: 'AssortmentGroup', ...pag }
        : null;
    const next: ExclusionAssortment | null = na
      ? { __typename: 'Assortment', ...na }
      : nag
        ? { __typename: 'AssortmentGroup', ...nag }
        : null;

    if (previous && next) {
      const key: AssortmentExclusionKey = `${previous.__typename === 'Assortment' ? 'A' : 'G'}${previous.name}-${next.__typename === 'Assortment' ? 'A' : 'G'}${next.name}`;
      const value: VoyageExclusionItem = {
        previous,
        next,
        voyages,
      };
      voyageExclusionsMap.set(key, value);
    }
  });

  const vesselExclusionItems: VesselExclusionItem[] = filteredMap(
    compact(
      vesselAttributeAssortmentExclusions
        ?.allVesselAttributeAssortmentExclusions?.nodes,
    ),
    (x) => {
      const exclusion: VesselExclusionItem['exclusion'] | null =
        x.assortmentByAssortmentId
          ? {
              __typename: 'Assortment' as const,
              ...x.assortmentByAssortmentId,
            }
          : x.assortmentGroupByAssortmentGroupId
            ? {
                __typename: 'AssortmentGroup' as const,
                ...x.assortmentGroupByAssortmentGroupId,
              }
            : null;
      if (x.vesselAttributeByVesselAttributeId && exclusion) {
        return {
          vesselAttribute: x.vesselAttributeByVesselAttributeId,
          exclusion,
        };
      } else {
        return NONE;
      }
    },
  );

  const vesselExclusionsMap = mapGroupBy<number, VesselExclusionItem>(
    vesselExclusionItems,
    (vae) => vae.vesselAttribute.id,
  );

  return {
    voyageExclusionsMap,
    vesselExclusionsMap,
  };
}
